import { useTranslation } from 'react-i18next';

import { DownOutlined, UpOutlined } from '@ant-design/icons';

import * as St from './NavBarPricing.styles';

import { MinimumPurchaseNotification } from '~/UI/components/molecules/MinimumPurchaseNotification/MinimumPurchaseNotification';
import { useOrderInvoice } from '~/UI/pages/OrderSummaryPage/hooks';
import { IMAGE_SIZE } from '~/core/constants/constants';
import { FocusElement, StepName } from '~/core/enums';
import { useSmallJobs } from '~/core/hooks/useSmallJobs';
import { imgUrl } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import {
  resetFocusElement,
  selectFocusElement,
  toggleFocusElement,
} from '~/redux/slices/layout';
import {
  checkEnabledStepsByListener,
  goToStep,
  selectSteps,
} from '~/redux/slices/stepper';
import CommonButton from '~atoms/CommonButton';
import OrderInvoice from '~organisms/OrderInvoice';

const selectEnabledStepsByListener = checkEnabledStepsByListener([
  StepName.designEdge,
  StepName.designCornerRadius,
  StepName.orderSummary,
]);

const NavBarPricing = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const focusElement = useAppSelector(selectFocusElement);
  const steps = useAppSelector(selectSteps);
  const [isEdgeStepEnabled, isCornerStepEnabled, isOrderStepEnabled] =
    useAppSelector(selectEnabledStepsByListener);
  const selectedColor = useAppSelector(selectSelectedColor);

  const isPriceDropdownOpen = focusElement === FocusElement.ORDER_SUMMARY;

  const { useMinQtyAreaForPricing, smallJobMinimumArea } = useSmallJobs();
  const { displayTotal } = useOrderInvoice();

  const fallbackBeautyShotImageUrl = '/public/assets/images/EstimateListBG.jpg';

  const beautyShotImageUrl: string = selectedColor?.beautyShotImageUrl
    ? imgUrl(selectedColor.beautyShotImageUrl, IMAGE_SIZE.LARGE)
    : fallbackBeautyShotImageUrl;

  const goToSummaryPage = () => {
    dispatch(resetFocusElement());
    dispatch(goToStep(StepName.orderSummary));
  };

  return (
    <>
      <St.Container
        onClick={() => dispatch(toggleFocusElement(FocusElement.ORDER_SUMMARY))}
        data-is-open={isPriceDropdownOpen}
      >
        <span>{t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.TOTAL')}</span>
        <div>
          <strong>{displayTotal}</strong>
          &nbsp;
          <DownOutlined />
        </div>
      </St.Container>
      {isPriceDropdownOpen && (
        <St.DropdownContainer onClick={() => dispatch(resetFocusElement())}>
          <St.DropdownCard onClick={(e) => e.stopPropagation()}>
            <St.DropdownImageContainer
              style={{
                backgroundImage: `url("${beautyShotImageUrl}")`,
              }}
            />
            <St.DropdownInvoiceContainer>
              <OrderInvoice tableMaxHeight="380rem" />

              <St.NotificationContainer>
                <MinimumPurchaseNotification
                  useMinQtyAreaForPricing={useMinQtyAreaForPricing}
                  smallJobMinimumArea={smallJobMinimumArea}
                />
              </St.NotificationContainer>

              <St.DropdownButtonContainer>
                <CommonButton
                  disabled={!isOrderStepEnabled}
                  onClick={goToSummaryPage}
                  style={{ fontSize: '20rem' }}
                >
                  {t('BUTTON.CHECKOUT')}
                </CommonButton>
                {!isOrderStepEnabled && (
                  <St.ErrorContainer>
                    {steps.includes(StepName.designEdge) && !isEdgeStepEnabled
                      ? t('VALIDATION_MESSAGE.REQUIRE_SHAPE')
                      : (steps.includes(StepName.designCornerRadius) &&
                            !isCornerStepEnabled) ||
                          !steps.includes(StepName.designCornerRadius)
                        ? t('VALIDATION_MESSAGE.REQUIRE_EDGE')
                        : t('VALIDATION_MESSAGE.REQUIRE_CORNER')}
                  </St.ErrorContainer>
                )}
              </St.DropdownButtonContainer>
            </St.DropdownInvoiceContainer>
          </St.DropdownCard>
          <St.CloseContainer>
            <UpOutlined />
            &nbsp;
            <span>Close</span>
          </St.CloseContainer>
        </St.DropdownContainer>
      )}
    </>
  );
};

export default NavBarPricing;
